import type { Kot, KotStatus } from "@luckymenu/core";

/**
 * KOTs on screen (OP-09; printing is Phase 5): the kitchen's list with
 * "Done", and the tickets of one order. Pure, shared by both staff apps.
 */

export type KitchenView = {
	/** Waiting to be made, oldest first: the kitchen works from the top. */
	open: Kot[];
	/** Made, most recent first. */
	done: Kot[];
};

export function kitchenView(kots: readonly Kot[]): KitchenView {
	const unique = new Map(kots.map((k) => [k.id, k]));
	const all = [...unique.values()];
	return {
		open: all
			.filter((k) => k.status === "open")
			.sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.kotNo - b.kotNo),
		done: all
			.filter((k) => k.status === "done")
			.sort(
				(a, b) =>
					(b.doneAt ?? b.createdAt).localeCompare(a.doneAt ?? a.createdAt) || b.kotNo - a.kotNo,
			),
	};
}

/** Changes on their way: KOT ID to the status it is being set to. */
export type PendingKots = ReadonlyMap<string, KotStatus>;

/** KOTs as they will be once the changes on their way are through. */
export function withKotChanges(kots: readonly Kot[], pending: PendingKots, now: string): Kot[] {
	if (pending.size === 0) return [...kots];
	return kots.map((k) => {
		const status = pending.get(k.id);
		if (!status || status === k.status) return k;
		return { ...k, status, doneAt: status === "done" ? now : null };
	});
}

/** Replaces a KOT (the server's answer) wherever it is in a list. */
export function withKot(kots: readonly Kot[], kot: Kot): Kot[] {
	return kots.map((k) => (k.id === kot.id ? kot : k));
}

/** Units on a ticket, for "5 items". */
export function kotUnits(kot: Kot): number {
	return kot.lines.reduce((n, l) => n + l.quantity, 0);
}
